import { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSeoMeta } from '@/hooks/useSeoMeta';
import api from '@/lib/api';
import { trackCheckout } from '@/lib/analytics';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CheckCircle, Shield, FileText, ArrowRight, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

export default function ConfirmationPage() {
  const { orderId } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);

  // Instant payload from sessionStorage (written by CheckoutPage before navigation)
  const [instantPayload, setInstantPayload] = useState(null);

  // Fire the GA4/Meta purchase beacon exactly once per confirmed order (ANL-01).
  // The authoritative payment_succeeded first-party event is emitted server-side.
  const purchaseTracked = useRef(false);

  useSeoMeta({ seo_title: 'Order Confirmed - The Fashion Detective' });

  // Load instant payload on mount (before API fetch completes)
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('tfd_last_confirmation');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.order_id === orderId) {
          setInstantPayload(parsed);
        }
      }
    } catch {}
  }, [orderId]);

  // Hydrate from API
  useEffect(() => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    api.get(`/orders/${orderId}`)
      .then(r => {
        setOrder(r.data);
        // Clean up sessionStorage after successful hydration
        try { sessionStorage.removeItem('tfd_last_confirmation'); } catch {}
      })
      .catch(err => {
        setErrorStatus(err.response?.status || null);
        setError(err.response?.data?.detail || 'Could not load order details');
      })
      .finally(() => setLoading(false));
  }, [orderId]);

  // Fire the purchase conversion once, from whichever payload arrives first.
  useEffect(() => {
    if (purchaseTracked.current) return;
    const src = instantPayload || order;
    if (!src) return;
    const orderNumber = src.order_number || orderId;
    const totalCents = src.total_cents || 0;
    const itemCount = (src.items_summary || src.items || []).length;
    purchaseTracked.current = true;
    trackCheckout(orderNumber, totalCents, itemCount);
  }, [instantPayload, order, orderId]);

  // Determine what to display: API data > instant payload > loading > error
  const displayOrder = order;
  const displayItems = order?.items || [];
  const hasInstant = !!instantPayload;
  const canUseInstantFallback = hasInstant && (authLoading || isAuthenticated);
  const authBlocked = !authLoading && (errorStatus === 401 || errorStatus === 403 || !isAuthenticated);

  // CASE 1: Still loading API, but we have instant payload — show it immediately
  if ((loading || authLoading) && canUseInstantFallback) {
    return (
      <div data-testid="confirmation-page">
        <ConfirmationHero
          orderNumber={instantPayload.order_number || orderId}
        />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <Card className="bg-white border-stone/50 mb-6">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-ink mb-4">Order Details</h3>
              <div className="space-y-3">
                {(instantPayload.items_summary || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {item.service_family === 'authentication'
                        ? <Shield className="h-3.5 w-3.5" style={{ color: '#0E6B5D' }} />
                        : <FileText className="h-3.5 w-3.5" style={{ color: '#A36A3A' }} />
                      }
                      <span className="text-sm text-ink">{item.item_title}</span>
                      <Badge variant="outline" className="text-[10px]">{item.category_name}</Badge>
                    </div>
                    <span className="text-sm text-ink">${((item.total_cents || 0) / 100).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="flex justify-between text-sm font-semibold">
                <span>Total Paid</span>
                <span>${((instantPayload.total_cents || 0) / 100).toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>
          <WhatHappensNext />
          <ConfirmationActions />
        </div>
      </div>
    );
  }

  // CASE 2: Still loading, no instant payload
  if (loading || authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // CASE 3: API failed, but we have instant payload — show it with a note
  if (!displayOrder && canUseInstantFallback && !authBlocked) {
    return (
      <div data-testid="confirmation-page">
        <ConfirmationHero orderNumber={instantPayload.order_number || orderId} />
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <Card className="bg-white border-stone/50 mb-6">
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-ink mb-4">Order Details</h3>
              <div className="space-y-3">
                {(instantPayload.items_summary || []).map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {item.service_family === 'authentication'
                        ? <Shield className="h-3.5 w-3.5" style={{ color: '#0E6B5D' }} />
                        : <FileText className="h-3.5 w-3.5" style={{ color: '#A36A3A' }} />
                      }
                      <span className="text-sm text-ink">{item.item_title}</span>
                    </div>
                    <span className="text-sm text-ink">${((item.total_cents || 0) / 100).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <Separator className="my-4" />
              <div className="flex justify-between text-sm font-semibold">
                <span>Total Paid</span>
                <span>${((instantPayload.total_cents || 0) / 100).toFixed(2)}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" /> We&apos;re still syncing the full order view in your account.
              </p>
            </CardContent>
          </Card>
          <WhatHappensNext />
          <ConfirmationActions />
        </div>
      </div>
    );
  }

  // CASE 4: API failed, no instant payload — explicit error state (never blank)
  if (!displayOrder) {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center" data-testid="confirmation-error">
        <AlertCircle className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
        <h1 className="font-display text-2xl font-medium text-ink mb-2">{authBlocked ? 'Sign in to view this order' : 'Order Details Unavailable'}</h1>
        <p className="text-sm text-muted-foreground mb-4">
          {authBlocked ? 'Order confirmation now lives in your authenticated account. Sign in to view this order.' : (error || 'We could not load your order details right now.')}
        </p>
        {authBlocked ? null : (
          <p className="text-xs text-muted-foreground mb-6">
            Your order was placed successfully. You can view it from your orders dashboard.
          </p>
        )}
        <div className="flex gap-3 justify-center">
          {authBlocked ? (
            <Link to="/account/sign-in">
              <Button className="gap-2 rounded-[12px]" data-testid="confirmation-signin-button">
                Sign In <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          ) : (
            <>
              <Button variant="outline" onClick={() => window.location.reload()} className="gap-2" data-testid="confirmation-retry-button">
                <RefreshCw className="h-4 w-4" /> Retry
              </Button>
              <Link to="/account/orders">
                <Button className="gap-2 rounded-[12px]" data-testid="confirmation-view-orders-button">
                  View My Orders <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </>
          )}
        </div>
      </div>
    );
  }

  // CASE 5: API success — full order data
  return (
    <div data-testid="confirmation-page">
      <ConfirmationHero orderNumber={displayOrder.order_number} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Card className="bg-white border-stone/50 mb-6">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-ink mb-4">Order Details</h3>
            <div className="space-y-3">
              {displayItems.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {item.service_family === 'authentication'
                      ? <Shield className="h-3.5 w-3.5" style={{ color: '#0E6B5D' }} />
                      : <FileText className="h-3.5 w-3.5" style={{ color: '#A36A3A' }} />
                    }
                    <span className="text-sm text-ink">{item.item_title || 'Untitled'}</span>
                    <Badge variant="outline" className="text-[10px]">{item.category_name}</Badge>
                    {item.is_expedited && <Badge className="text-[9px]" style={{ background: 'rgba(163,106,58,0.10)', color: '#A36A3A' }}>Expedited</Badge>}
                  </div>
                  <span className="text-sm text-ink">${((item.total_cents || 0) / 100).toFixed(2)}</span>
                </div>
              ))}
            </div>
            <Separator className="my-4" />
            <div className="flex justify-between text-sm font-semibold">
              <span>Total Paid</span>
              <span>${((displayOrder.total_cents || 0) / 100).toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>

        <WhatHappensNext />
        <ConfirmationActions />
      </div>
    </div>
  );
}


// ─── Extracted sub-components ────────────────────────────────────────

function ConfirmationHero({ orderNumber }) {
  return (
    <section style={{ background: 'linear-gradient(135deg, hsl(36 33% 98%) 0%, hsl(36 22% 94%) 55%, hsl(35 19% 88%) 100%)' }}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}>
          <CheckCircle className="h-12 w-12 mx-auto mb-4" style={{ color: '#0E6B5D' }} />
          <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-[-0.02em] text-ink mb-2">
            Order Confirmed
          </h1>
          <p className="text-sm text-muted-foreground mb-1">Thank you for your order!</p>
          {orderNumber && (
            <p className="font-mono text-sm text-ink" data-testid="order-number">{orderNumber}</p>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function WhatHappensNext() {
  return (
    <Card className="bg-white border-stone/50 mb-6">
      <CardContent className="p-5">
        <h3 className="text-sm font-semibold text-ink mb-2">What Happens Next?</h3>
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li>Our expert authenticators will begin reviewing your item(s).</li>
          <li>Standard results arrive within 1 business day. Expedited results in under 2 hours during business hours.</li>
          <li>You'll receive email updates as your items are reviewed.</li>
          <li>Visit your account at any time to check status and access documents.</li>
        </ul>
      </CardContent>
    </Card>
  );
}

function ConfirmationActions() {
  return (
    <div className="flex flex-wrap gap-3 justify-center">
      <Link to="/account/orders">
        <Button variant="outline" className="gap-2 rounded-[12px]" data-testid="confirmation-primary-orders-button">
          View My Orders <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
      <Link to="/order/submit">
        <Button className="gap-2 rounded-[12px] active:scale-[0.98]" data-testid="confirmation-submit-another-button">
          Submit Another Item <ArrowRight className="h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
}
